import { createContext, useCallback, useContext, useState } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Avisos temporales en la esquina de la pantalla.
 *
 * Vive en su propio archivo porque mezclar componentes y hooks exportados
 * rompe el recargado en caliente de Vite.
 */

interface Aviso {
  id: number;
  tipo: 'exito' | 'error';
  mensaje: string;
}

const AvisosContext = createContext<{
  mostrar: (tipo: Aviso['tipo'], mensaje: string) => void;
}>({ mostrar: () => {} });

export function ProveedorAvisos({ children }: { children: React.ReactNode }) {
  const [avisos, setAvisos] = useState<Aviso[]>([]);

  const mostrar = useCallback((tipo: Aviso['tipo'], mensaje: string) => {
    const id = Date.now() + Math.random();
    setAvisos((actuales) => [...actuales, { id, tipo, mensaje }]);
    // Los errores duran más: hay que alcanzar a leerlos.
    setTimeout(() => setAvisos((a) => a.filter((x) => x.id !== id)), tipo === 'error' ? 6000 : 3500);
  }, []);

  return (
    <AvisosContext.Provider value={{ mostrar }}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col gap-2"
      >
        {avisos.map((aviso) => (
          <div
            key={aviso.id}
            className={cn(
              'pointer-events-auto flex max-w-sm items-start gap-2 rounded-kick border px-4 py-3 text-sm shadow-lg animate-slide-up',
              aviso.tipo === 'exito'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-red-200 bg-red-50 text-red-800',
            )}
          >
            {aviso.tipo === 'exito' ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            )}
            <span>{aviso.mensaje}</span>
          </div>
        ))}
      </div>
    </AvisosContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAvisos() {
  const { mostrar } = useContext(AvisosContext);
  return {
    exito: (mensaje: string) => mostrar('exito', mensaje),
    error: (mensaje: string) => mostrar('error', mensaje),
  };
}
